/* global BigInt:readonly */
import * as Yup from 'yup';
import { setIn } from 'formik';

export const MAX_QUOTE_PRODUCTS = 100;
export const MAX_QUOTE_QUANTITY = 2147483647;
export const MAX_QUOTE_MONEY = '9999999999999.99';
const MAX_CENTS = BigInt('999999999999999');
let nextRowKey = 0;

export const newQuoteProduct = () => ({
  _key: 'new-product-' + (++nextRowKey),
  product_code: '', principal_id: '', category_id: '', qty: '', unit_price: '', total: '',
});

export const moneyCents = value => {
  const text = String(value ?? '');
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return BigInt(whole) * BigInt(100) + BigInt(fraction.padEnd(2, '0'));
};

export const centsAmount = cents => {
  const text = cents.toString().padStart(3, '0');
  return text.slice(0, -2) + '.' + text.slice(-2);
};

const integerQuantity = value => /^\d+$/.test(String(value ?? '')) ? BigInt(value) : null;

export const suggestedProductTotal = (qty, unitPrice) => {
  const quantity = integerQuantity(qty);
  const price = moneyCents(unitPrice);
  if (quantity === null || quantity <= BigInt(0) || price === null) return null;
  return centsAmount(quantity * price);
};

export const quoteProductSummary = products => {
  let quantity = BigInt(0);
  let total = BigInt(0);
  for (const row of products) {
    quantity += integerQuantity(row.qty) ?? BigInt(0);
    total += moneyCents(row.total) ?? BigInt(0);
  }
  return { qty: quantity.toString(), total: centsAmount(total) };
};

export const buildQuoteProducts = quote => {
  if (Array.isArray(quote?.products) && quote.products.length) {
    return quote.products.map((row, index) => ({
      _key: 'saved-product-' + (row.id ?? index),
      ...(row.id != null ? { id: row.id } : {}),
      product_code: row.product_code ?? '',
      principal_id: String(row.principal_id ?? quote.principal_id ?? ''),
      category_id: String(row.category_id ?? quote.category_id ?? ''),
      qty: String(row.qty ?? ''),
      unit_price: String(row.unit_price ?? ''),
      total: String(row.total ?? ''),
    }));
  }
  const qty = integerQuantity(quote?.qty);
  const total = moneyCents(quote?.total);
  const unitPrice = qty !== null && qty > BigInt(0) && total !== null
    ? centsAmount((total + qty / BigInt(2)) / qty) : '';
  return [{
    _key: 'legacy-product-' + (quote?.id ?? 'draft'),
    product_code: quote?.product_code ?? '',
    principal_id: String(quote?.principal_id ?? ''),
    category_id: String(quote?.category_id ?? ''),
    qty: String(quote?.qty ?? ''),
    unit_price: unitPrice,
    total: String(quote?.total ?? ''),
  }];
};

const moneySchema = label => Yup.string().required(label + ' is required')
  .matches(/^\d+(?:\.\d{1,2})?$/, 'Enter a non-negative amount with up to 2 decimal places')
  .test('money-limit', label + ' cannot exceed ' + MAX_QUOTE_MONEY, value => {
    const cents = moneyCents(value);
    return cents === null || cents <= MAX_CENTS;
  });

const productsSchema = (classificationRequired) => Yup.array().required('Add at least one product')
  .min(1, 'Add at least one product').max(MAX_QUOTE_PRODUCTS, 'A quote can contain up to 100 products')
  .of(Yup.object({
    principal_id: classificationRequired ? Yup.string().required('Principal is required').notOneOf(['0'], 'Select a principal') : Yup.string(),
    category_id: classificationRequired ? Yup.string().required('Product category is required').notOneOf(['0'], 'Select a product category') : Yup.string(),
    product_code: Yup.string().trim().required('Product code is required').max(1000, 'Product code cannot exceed 1000 characters'),
    qty: Yup.string().required('Quantity is required')
      .matches(/^[1-9]\d*$/, 'Quantity must be a positive whole number')
      .test('quantity-limit', 'Quantity cannot exceed ' + MAX_QUOTE_QUANTITY, value => {
        const quantity = integerQuantity(value);
        return quantity === null || quantity <= BigInt(MAX_QUOTE_QUANTITY);
      }),
    unit_price: moneySchema('Unit price'),
    total: moneySchema('Total'),
  }))
  .test('quantity-sum', 'Combined quantity cannot exceed ' + MAX_QUOTE_QUANTITY, rows =>
    !rows || BigInt(quoteProductSummary(rows).qty) <= BigInt(MAX_QUOTE_QUANTITY))
  .test('total-sum', 'Quote total cannot exceed ' + MAX_QUOTE_MONEY, rows =>
    !rows || moneyCents(quoteProductSummary(rows).total) <= MAX_CENTS);

export const quoteProductsSchema = productsSchema(true);
export const poProductsSchema = productsSchema(false);

export const buildQuotePayload = values => {
  const { products, product_code, qty, total, ...quote } = values;
  return {
    ...quote,
    principal_id: products[0]?.principal_id,
    category_id: products[0]?.category_id,
    products: products.map(row => ({
      ...(row.id != null ? { id: row.id } : {}),
      product_code: row.product_code.trim(),
      principal_id: row.principal_id,
      category_id: row.category_id,
      qty: row.qty,
      unit_price: row.unit_price,
      total: row.total,
    })),
  };
};

export const quoteFormErrors = errors => Object.entries(errors || {}).reduce(
  (result, [field, messages]) => setIn(result, field, Array.isArray(messages) ? messages[0] : messages), {}
);
